import { useCallback, useMemo } from "react";
import { DEFAULT_POINT_DETAIL_IDS } from "../utils/pointDetails";
import { DEFAULT_MAP_TILE_ID, MAP_TILE_LAYERS, MapTileId } from "@/constants/MapTiles";
import { useUserState } from "@/hooks/userState/useUserState";

/** Where the map was looking, so the next visit starts there. */
export interface RouteMapView {
  latitude: number;
  longitude: number;
  zoom: number;
}

/** The middle of Germany, for a reader who has never moved the map. */
export const DEFAULT_ROUTE_MAP_VIEW: RouteMapView = { latitude: 51.0, longitude: 10.0, zoom: 6 };

const DEFAULTS = {
  tileId: DEFAULT_MAP_TILE_ID as string,
  // `null` rather than the default view: the store only checks a stored value's type against the
  // default's, and a `null` default lets an object through for `toRouteMapView` to check field by field.
  view: null as RouteMapView | null,
  // `null` until the reader chooses, so a change to the defaults reaches everyone who never did.
  pointDetailIds: null as string[] | null,
};

function toTileId(value: string): MapTileId {
  return MAP_TILE_LAYERS.find(layer => layer.id === value)?.id ?? DEFAULT_MAP_TILE_ID;
}

/** A stored view, or the default when what is stored is not a complete one. */
export function toRouteMapView(value: unknown): RouteMapView {
  if (typeof value !== "object" || value === null) return DEFAULT_ROUTE_MAP_VIEW;
  const { latitude, longitude, zoom } = value as Partial<Record<keyof RouteMapView, unknown>>;
  const isNumber = (n: unknown): n is number => typeof n === "number" && Number.isFinite(n);
  return isNumber(latitude) && isNumber(longitude) && isNumber(zoom)
    ? { latitude, longitude, zoom }
    : DEFAULT_ROUTE_MAP_VIEW;
}

/** The chosen point details, or the defaults when nothing usable is stored. */
export function toPointDetailIds(value: unknown): string[] {
  return Array.isArray(value) && value.every(id => typeof id === "string")
    ? (value as string[])
    : DEFAULT_POINT_DETAIL_IDS;
}

/** The reader's own basemap, last view and point details on the route map. */
export function useRouteMapSettings() {
  const { value, setValue, isReady } = useUserState("route:map", DEFAULTS);

  const tileId = toTileId(value.tileId);
  const view = useMemo(() => toRouteMapView(value.view), [value.view]);
  const pointDetailIds = useMemo(() => toPointDetailIds(value.pointDetailIds), [value.pointDetailIds]);

  const setTileId = useCallback(
    (next: MapTileId) => setValue(current => ({ ...current, tileId: next })),
    [setValue],
  );
  const setView = useCallback(
    (next: RouteMapView) => setValue(current => ({ ...current, view: next })),
    [setValue],
  );

  const setPointDetailIds = useCallback(
    (next: string[]) => setValue(current => ({ ...current, pointDetailIds: next })),
    [setValue],
  );

  return { tileId, setTileId, view, setView, pointDetailIds, setPointDetailIds, isReady };
}
